import {CookieService} from 'ngx-cookie-service';
import {Constants} from '../constants';
import {Utils} from './utils';
import get from 'lodash-es/get';
import invert from 'lodash-es/invert';
import includes from 'lodash-es/includes';
import { Injectable } from '@angular/core';

@Injectable()
export class LocaleHandler {
    /**
     * Locale cookie name
     * @var {string}
     */
    private readonly _localeCookieName: string = Constants.COOKIE_LOCALE;

    /**
     * Current locale
     * @var {string}
     */
    private _currentLocale: string;

    /**
     * Current preferred language
     * @var {string}
     */
    private _currentPreferredLanguage: string;

    /**
     * Default preferred language
     * @var {string}
     */
    private _defaultPreferredLanguage: string = Constants.DEFAULT_PREFERRED_LANGUAGE;

    /**
     * Class constructor
     *
     * @param _cookieService
     */
    constructor(
        private _cookieService: CookieService
    ) {
        if (!this._isLocaleCookieSet()) {
            this._setLocaleCookie();
        }

        this._setPreferredLanguage();
    }

    /**
     * Gets current locale
     *
     * @returns {string}
     */
    public getCurrentLocale(): string {
        return this._currentLocale;
    }

    /**
     * Gets the current locale language
     *
     * @returns {string}
     */
    public getCurrentLanguage(): string {
        return this._currentLocale.split('-')[0];
    }

    /**
     * Gets the current preferred language
     *
     * @returns {string}
     */
    public getCurrentPreferredLanguage(): string {
        const supportedLocalesMap = invert(Constants.SUPPORTED_LOCALES);
        let mappedPreferredLanguage;

        for (const key in supportedLocalesMap) {
            if (includes(key.split(','), this._currentPreferredLanguage)) {
                mappedPreferredLanguage = supportedLocalesMap[key];
            }
        }

        return mappedPreferredLanguage || this._defaultPreferredLanguage;
    }

    /**
     * Checks if locale cookie is set
     *
     * @private
     *
     * @returns {boolean}
     */
    private _isLocaleCookieSet(): boolean {
        return this._cookieService.check(this._localeCookieName);
    }

    /**
     * Checks if preferred language cookie is set
     *
     * @private
     *
     * @returns {boolean}
     */
    private _isPreferredLanguageCookieSet(): boolean {
        return this._cookieService.check(Constants.COOKIE_LANGUAGE);
    }

    /**
     * Sets the locale cookie
     *
     * @return {void}
     */
    private _setLocaleCookie(): void {
        this._cookieService.set(
            this._localeCookieName,
            JSON.stringify({
                contentLocale: this._defaultPreferredLanguage
            }),
            0,
            '',
            '',
            false,
            'Strict'
        );
    }

    /**
     * Sets the preferred language based on the languageSelection cookie
     *
     * @private
     *
     * @returns {void}
     */
    private _setPreferredLanguage(): void {
        let languageCookie;

        if (this._isPreferredLanguageCookieSet()) {
            languageCookie = this._parseJsonCookie(Constants.COOKIE_LANGUAGE);
        }

        this._currentPreferredLanguage = this._normalizePreferredLanguage(
            get(
                languageCookie,
                Constants.PREFERRED_LANGUAGE,
                this._getPreferredLanguageFromLocaleCookie()
            )
        );
    }

    /**
     * Gets the preferred language based on the locale cookie
     *
     * @private
     *
     * @returns {string}
     */
    private _getPreferredLanguageFromLocaleCookie(): string {
        const localeCookie = this._parseJsonCookie(Constants.COOKIE_LOCALE);

        return get(
            localeCookie,
            Constants.CONTENT_LOCALE,
            'en-us'
        );
    }

    /**
     * Normalizes the current preferred language
     *
     * @param preferredLanguage
     * @private
     *
     * @returns {string}
     */
    private _normalizePreferredLanguage(preferredLanguage: string): string {
        return preferredLanguage.trim().replace('_', '-').toLowerCase();
    }

    /**
     * Parses the content of a cookie
     *
     * @returns {any}
     */
    private _parseJsonCookie(cookieName: string): any {
        const rawCookieContent = this._cookieService.get(cookieName);
        let parsedCookieContent;

        try {
            parsedCookieContent = JSON.parse(rawCookieContent);
        } catch (e) {
            parsedCookieContent = rawCookieContent;
        }

        return parsedCookieContent;
    }
}
